import React, { useEffect, useState } from "react";
import Checkbox from "../../components/inputs/checkbox";
import CustomSelect from "../../components/inputs/select";
import { toast } from "react-toastify";
import { createEtablissementHoraires } from "../../store/slices/etablissementSlice";
import { useDispatch } from "react-redux";
import { formatUpdateDate } from "../../utils/utils";

// Time comparison utility
const compareTimes = (time1, time2) => {
  const [hours1, minutes1] = time1.split(':').map(Number);
  const [hours2, minutes2] = time2.split(':').map(Number);
  
  if (hours1 !== hours2) return hours1 - hours2;
  return minutes1 - minutes2;
};

export const OpeningHoursForm = ({
  selectedEtablissement,
  initialSchedules = [],
  onSave,
  onSubmit,
  from,
  days = [
    "Lundi",
    "Mardi",
    "Mercredi",
    "Jeudi",
    "Vendredi",
    "Samedi",
    "Dimanche",
  ],
}) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(from === "account" ? false : true);
  const [timeErrors, setTimeErrors] = useState({
    openingClosing: '',
    pause: '',
  });
  const [isCompleted, setIsCompleted] = useState(true); // Add this state
  // Options pour les heures et minutes
  const hours = Array.from({ length: 24 }, (_, i) => ({
    label: i.toString().padStart(2, "0"),
    value: i.toString().padStart(2, "0"),
  }));

  const minutes = Array.from({ length: 60 }, (_, i) => i)
    .filter(minute => minute % 15 === 0)
    .map(minute => ({
      label: minute.toString().padStart(2, "0"),
      value: minute.toString().padStart(2, "0"),
    }));

  // State pour les plages horaires enregistrées
  const [schedules, setSchedules] = useState([]);

  // Initialize schedules when initialSchedules changes
  useEffect(() => {
    setIsCompleted(selectedEtablissement?.horaires?.length>0 ||initialSchedules?.length>0)
    if (initialSchedules && initialSchedules.length > 0) {
      setSchedules(initialSchedules);
    }
  }, [initialSchedules]);

  // State pour la sélection en cours
  const [currentSelection, setCurrentSelection] = useState({
    selectedDays: [],
    openingHour: "08",
    openingMinute: "00",
    pauseHour: "12",
    pauseMinute: "00",
    endPauseHour: "14",
    endPauseMinute: "00",
    closingHour: "18",
    closingMinute: "00",
    continuous: false,
    closedAllDay: false,
  });

  const handleSubmit = async () => {
    const formattedSchedules = schedules.map((schedule) => {
      return formatSchedule(schedule);
    });
    const data = formattedSchedules.flat();
    
    if (data.length > 0) {
      await dispatch(createEtablissementHoraires(data)).unwrap();
       toast.success("Informations de l'établissement mises à jour avec succès"); // Success toast
    } else {
      toast.error("Aucune donnée à sauvegarder");
    }
  };

  // Gestion de la sélection des jours
  const handleDaySelection = (day) => {
    setCurrentSelection((prev) => {
      const isSelected = prev.selectedDays.includes(day);
      return {
        ...prev,
        selectedDays: isSelected
          ? prev.selectedDays.filter((selectedDay) => selectedDay !== day)
          : [...prev.selectedDays, day],
      };
    });
  };

  // Gestion des changements d'horaires
  const handleTimeChange = (field, value) => {
    setCurrentSelection((prev) => ({ ...prev, [field]: value }));
  };

  const formatSchedule = (schedule) => {
    const isClosed = schedule.closedAllDay;
    const isContinuous = schedule.continuous && !isClosed;

    return schedule.days.map((day) => ({
      jour: day.toLowerCase(),
      horaire_ouverture: isClosed ? null : schedule.opening,
      horaire_fermeture: isClosed ? null : schedule.closing,
      pauseStart: isClosed || isContinuous ? null : schedule.pauseStart,
      pauseEnd: isClosed || isContinuous ? null : schedule.pauseEnd,
      continuous: isContinuous,
      fermee: Boolean(isClosed),
    }));
  };

  // Validate times before saving
  const validateTimes = () => {
    const errors = {
      openingClosing: '',
      pause: '',
    };

    if (currentSelection.closedAllDay) {
      setTimeErrors(errors);
      return true;
    }

    const openingTime = `${currentSelection.openingHour}:${currentSelection.openingMinute}`;
    const closingTime = `${currentSelection.closingHour}:${currentSelection.closingMinute}`;
    
    // Validate opening vs closing
    if (compareTimes(openingTime, closingTime) >= 0) {
      errors.openingClosing = "L'heure de fermeture doit être après l'heure d'ouverture";
    }

    // Validate pause times (only if not continuous and not closed all day)
    if (!currentSelection.continuous && !currentSelection.closedAllDay) {
      const pauseStart = `${currentSelection.pauseHour}:${currentSelection.pauseMinute}`;
      const pauseEnd = `${currentSelection.endPauseHour}:${currentSelection.endPauseMinute}`;
      
      if (compareTimes(pauseStart, pauseEnd) >= 0) {
        errors.pause = "L'heure de fin de pause doit être après l'heure de début";
      }
      
      if (compareTimes(openingTime, pauseStart) >= 0) {
        errors.pause = "La pause doit commencer après l'ouverture";
      }
      
      if (compareTimes(pauseEnd, closingTime) >= 0) {
        errors.pause = "La pause doit se terminer avant la fermeture";
      }
    }

    setTimeErrors(errors);
    return Object.values(errors).every(error => !error);
  };

  // Sauvegarde de la plage horaire
  const saveSchedule = () => {
    if (currentSelection.selectedDays.length === 0) {
      toast.error("Veuillez sélectionner au moins un jour");
      return;
    }

    if (!validateTimes()) {
      return;
    }

    const newSchedule = {
      id: Date.now(),
      days: [...currentSelection.selectedDays],
      opening: `${currentSelection.openingHour}:${currentSelection.openingMinute}`,
      closing: `${currentSelection.closingHour}:${currentSelection.closingMinute}`,
      pauseStart: currentSelection.continuous || currentSelection.closedAllDay
        ? null
        : `${currentSelection.pauseHour}:${currentSelection.pauseMinute}`,
      pauseEnd: currentSelection.continuous || currentSelection.closedAllDay
        ? null
        : `${currentSelection.endPauseHour}:${currentSelection.endPauseMinute}`,
      continuous: currentSelection.continuous,
      closedAllDay: currentSelection.closedAllDay,
    };

    const updatedSchedules = [...schedules, newSchedule];
    setSchedules(updatedSchedules);

    // Réinitialiser la sélection
    setCurrentSelection({
      selectedDays: [],
      openingHour: "08",
      openingMinute: "00",
      pauseHour: "12",
      pauseMinute: "00",
      endPauseHour: "14",
      endPauseMinute: "00",
      closingHour: "18",
      closingMinute: "00",
      continuous: false,
      closedAllDay: false,
    });

    setTimeErrors({ openingClosing: '', pause: '' });

    if (onSave) {
      onSave(updatedSchedules);
    }
  };

  // Suppression d'une plage horaire
  const removeSchedule = (id) => {
    const updatedSchedules = schedules.filter((s) => s.id !== id);
    setSchedules(updatedSchedules);

    if (onSave) {
      onSave(updatedSchedules);
    }
  };

  // Vérifie si un jour est déjà utilisé dans une plage horaire
  const isDayUsed = (day) => {
    return schedules.some((schedule) => schedule.days.includes(day));
  };

  const renderTimeInput = ({ label, hourKey, minuteKey, isPause = false }) => (
    <div className="col-md-6 mt-8">
      <p className="legend grey66">{label}</p>
      <div className="row">
        <div className="col-md-6">
          <CustomSelect
            selectedValue={currentSelection[hourKey]}
            options={hours}
            onChange={(value) => {
              handleTimeChange(hourKey, value);
              validateTimes();
            }}
            disabled={
              currentSelection.continuous && isPause ||
              currentSelection.closedAllDay
            }
          />
        </div>
        <div className="col-md-6">
          <CustomSelect
            selectedValue={currentSelection[minuteKey]}
            options={minutes}
            onChange={(value) => {
              handleTimeChange(minuteKey, value);
              validateTimes();
            }}
            disabled={
              currentSelection.continuous && isPause ||
              currentSelection.closedAllDay
            }
          />
        </div>
      </div>
      {isPause && timeErrors.pause && (
        <p className="text-danger small mt-1">{timeErrors.pause}</p>
      )}
      {!isPause && hourKey === 'closingHour' && timeErrors.openingClosing && (
        <p className="text-danger small mt-1">{timeErrors.openingClosing}</p>
      )}
    </div>
  );

  return (
    <div className="">
      <div className="d-flex flex-wrap justify-content-between align-items-center">
        {from === "account" ? (
          <h4 className="grey500">
            Jours et horaires d'ouverture
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <img
                src="/images/chevron-down.svg"
                className={`w-6 h-6 ${!isExpanded ? "rotate180" : ""}`}
                alt={isExpanded ? "Réduire" : "Ouvrir"}
              />
            </button>
            {!isCompleted && selectedEtablissement  ? <img src="/images/notCompleted.svg" alt="not completed icon" className="ml-8" />:''}

          </h4>
        ) : (
          <h3 className="grey500">Jours et horaires d'ouverture</h3>
        )}
        {from === "account" && (
          <div className="d-flex align-items-center">
            {selectedEtablissement?.updated_at && (
              <span className="body grey66 mr-16">
                Dernière mise à jour:{" "}
                {formatUpdateDate(selectedEtablissement.updated_at)}
              </span>
            )}
          </div>
        )}
      </div>
      {isExpanded && (
        <>
          <p className="body grey66 mt-16">
            Entrez les horaires d'ouverture de chaque jour, et indiquez vos
            jours fermés. Sélectionnez les jours avec le même horaire,
            indiquez-les puis validez votre plage horaire.
          </p>

          {/* Affichage des plages horaires enregistrées */}
          {schedules?.map((schedule) => (
            <div
              key={schedule.id}
              className="badge-pre p-16 mt-32 d-flex justify-content-between align-items-center"
            >
              <div>
                <p className="body grey500">
                  {days
                    .filter((d) => schedule?.days?.includes(d))
                    ?.map((d) => d)
                    ?.join(", ")}
                </p>
                <p className="body grey66">
                  {schedule.closedAllDay
                    ? "Fermé toute la journée"
                    : schedule.continuous
                    ? `${schedule?.opening} - ${schedule?.closing} (Ouverture continue)`
                    : `${schedule?.opening} - ${schedule?.pauseStart}, ${schedule?.pauseEnd}-${schedule?.closing}`}
                </p>
              </div>
              <button
                onClick={() => removeSchedule(schedule.id)}
                className="link-blue"
              >
                <img src="/images/trash.svg" alt="trash icon" />
              </button>
            </div>
          ))}

          {/* Sélection des jours */}
          <div className="days mt-32">
            <p className="legend grey66">Jours*</p>
            <div className="row">
              {days?.map((day) => (
                <div key={day} className="col-md-3 px-0">
                  <div
                    className={`day-ouv ${isDayUsed(day) ? "day-checked" : ""}`}
                  >
                    <Checkbox
                      id={`day-${day}`}
                      label={day}
                      checked={currentSelection.selectedDays.includes(day)}
                      onChange={() => handleDaySelection(day)}
                      disabled={isDayUsed(day)}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Sélection des horaires */}
            {!currentSelection.closedAllDay && (
              <div className="row mt-24">
                {renderTimeInput({
                  label: "Horaire d'ouverture*",
                  hourKey: "openingHour",
                  minuteKey: "openingMinute"
                })}
                {renderTimeInput({
                  label: "Horaire de pause*",
                  hourKey: "pauseHour",
                  minuteKey: "pauseMinute",
                  isPause: true
                })}
                {renderTimeInput({
                  label: "Horaire de fin de pause*",
                  hourKey: "endPauseHour",
                  minuteKey: "endPauseMinute",
                  isPause: true
                })}
                {renderTimeInput({
                  label: "Horaire de fermeture*",
                  hourKey: "closingHour",
                  minuteKey: "closingMinute"
                })}
              </div>
            )}

            {/* Options */}
            <div className="d-flex items-center mt-24">
              <div className="mr-24">
                <Checkbox
                  label="Ouverture continue"
                  id="continuous"
                  checked={currentSelection.continuous}
                  onChange={(checked) => {
                    handleTimeChange("continuous", !currentSelection.continuous);
                    handleTimeChange("closedAllDay", false);
                    setTimeErrors({ openingClosing: '', pause: '' });
                  }}
                />
              </div>
              <Checkbox
                id="closedAllDay"
                label="Fermé toute la journée"
                checked={currentSelection.closedAllDay}
                onChange={(checked) => {
                  handleTimeChange("closedAllDay", !currentSelection.closedAllDay);
                  if (checked) {
                    handleTimeChange("continuous", false);
                  }
                  setTimeErrors({ openingClosing: '', pause: '' });
                }}
              />
            </div>

            {/* Bouton de sauvegarde */}
            <button
              onClick={saveSchedule}
              className="twyny-cta-secondary w-100 mt-24"
              disabled={
                currentSelection.selectedDays.length === 0 ||
                timeErrors.openingClosing ||
                timeErrors.pause
              }
            >
              Enregistrer la plage horaire
            </button>
            {from === "account" && (
              <button
                type="submit"
                onClick={handleSubmit}
                className="twyny-cta-primary w-100 mt-24"
              >
                Enregistrer les modifications
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};